import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Form,
  Input,
  Select,
  DatePicker,
  Radio,
  Upload,
  Button,
  Card,
  message,
  Spin,
  Alert,
  Modal
} from 'antd';
import {
  UploadOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  PlusOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useStores } from '../../stores';
import { getAnimalNameWithEmoji } from '../../utils/animalEmojis';
import './AddEditPetPage.scss';

const { Option } = Select;

const { TextArea } = Input;

const MAX_FREE_PETS = 2;

const AddEditPetPage = observer(() => {
  const { t, i18n } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const { petStore } = useStores();
  const [form] = Form.useForm();
  const [suggestionForm] = Form.useForm();
  const [breedSuggestionForm] = Form.useForm();
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [suggestionModalVisible, setSuggestionModalVisible] = useState(false);
  const [breedSuggestionModalVisible, setBreedSuggestionModalVisible] = useState(false);
  const [submittingSuggestion, setSubmittingSuggestion] = useState(false);
  const [submittingBreedSuggestion, setSubmittingBreedSuggestion] = useState(false);
  const [petLimitReached, setPetLimitReached] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const isEditMode = !!id;

  useEffect(() => {
    const initializeForm = async () => {
      setInitializing(true);

      // Load categories and all breeds first
      await Promise.all([
        petStore.fetchAnimalCategories(),
        petStore.fetchAllBreeds()
      ]);

      // If add mode, check pet limit
      if (!isEditMode) {
        await checkPetLimit();
      }

      // If edit mode, load pet details after categories/breeds are loaded
      if (isEditMode) {
        await loadPetDetails();
      }

      setInitializing(false);
    };

    initializeForm();

    return () => {
      petStore.clearSelectedPet();
    };
  }, [id]);

  const checkPetLimit = async () => {
    // Fetch pets if not already loaded
    if (petStore.myPets.length === 0) {
      await petStore.fetchMyPets();
    }
    // Check if user has reached the limit
    if (petStore.myPets.length >= MAX_FREE_PETS) {
      setPetLimitReached(true);
    }
  };

  const loadPetDetails = async () => {
    const success = await petStore.fetchPetDetail(id);
    if (success && petStore.selectedPet) {
      const pet = petStore.selectedPet;

      // Set form values
      form.setFieldsValue({
        name: pet.name,
        animal_category: pet.animal_category,
        breed: pet.breed,
        microchip_code: pet.microchip_code,
        birthday: pet.birthday ? dayjs(pet.birthday) : null,
        size: pet.size
      });

      // Set selected category and filter breeds
      setSelectedCategory(pet.animal_category);
      petStore.filterBreedsByCategory(pet.animal_category);

      // Set photo preview if exists
      if (pet.photo) {
        setPhotoPreview(pet.photo);
      }
    }
  };

  const handleCategoryChange = (value) => {
    setSelectedCategory(value);
    // Clear breed selection
    form.setFieldsValue({ breed: null });
    // Filter breeds by category
    petStore.filterBreedsByCategory(value);
  };

  const handlePhotoChange = (info) => {
    const file = info.file.originFileObj || info.file;

    if (file) {
      // Validate file type
      const isImage = file.type?.startsWith('image/');
      if (!isImage) {
        message.error(t('pets.addEdit.validation.imageOnly'));
        return;
      }

      // Validate file size (max 5MB)
      const isLt5M = file.size / 1024 / 1024 < 5;
      if (!isLt5M) {
        message.error(t('pets.addEdit.validation.imageTooLarge'));
        return;
      }

      setPhotoFile(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setPhotoPreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);

    // Prepare data
    const petData = {
      name: values.name,
      animal_category: values.animal_category,
      breed: values.breed || null,
      microchip_code: values.microchip_code || '',
      birthday: values.birthday ? values.birthday.format('YYYY-MM-DD') : null,
      size: values.size || null
    };

    // Add photo if selected
    if (photoFile) {
      petData.photo = photoFile;
    }

    let result;
    if (isEditMode) {
      result = await petStore.updatePet(id, petData);
    } else {
      result = await petStore.createPet(petData);
    }

    setSubmitting(false);

    if (result.success) {
      message.success(isEditMode ? t('pets.addEdit.messages.updated') : t('pets.addEdit.messages.added'));
      navigate('/my-pets');
    } else {
      // Display detailed error message
      let errorMessage = isEditMode ? t('pets.addEdit.messages.updateFailed') : t('pets.addEdit.messages.addFailed');
      if (typeof result.error === 'object') {
        errorMessage = Object.entries(result.error)
          .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join(', ') : value}`)
          .join('\n');
      } else {
        errorMessage = result.error;
      }
      message.error(errorMessage);
    }
  };

  const handleCancel = () => {
    navigate('/my-pets');
  };

  const handleSuggestionSubmit = async (values) => {
    setSubmittingSuggestion(true);

    const result = await petStore.submitCategorySuggestion({
      suggested_name: values.suggested_name,
      description: values.description || ''
    });

    setSubmittingSuggestion(false);

    if (result.success) {
      message.success(t('pets.addEdit.messages.categorySuggestionSuccess'));
      suggestionForm.resetFields();
      setSuggestionModalVisible(false);
    } else {
      let errorMessage = t('pets.addEdit.messages.categorySuggestionFailed');
      if (typeof result.error === 'object') {
        errorMessage = Object.values(result.error).flat().join(', ');
      } else {
        errorMessage = result.error;
      }
      message.error(errorMessage);
    }
  };

  const handleBreedSuggestionSubmit = async (values) => {
    setSubmittingBreedSuggestion(true);

    const result = await petStore.submitBreedSuggestion({
      animal_category: selectedCategory,
      suggested_name: values.suggested_name,
      description: values.description || ''
    });

    setSubmittingBreedSuggestion(false);

    if (result.success) {
      message.success(t('pets.addEdit.messages.breedSuggestionSuccess'));
      breedSuggestionForm.resetFields();
      setBreedSuggestionModalVisible(false);
    } else {
      let errorMessage = t('pets.addEdit.messages.breedSuggestionFailed');
      if (typeof result.error === 'object') {
        errorMessage = Object.values(result.error).flat().join(', ');
      } else {
        errorMessage = result.error;
      }
      message.error(errorMessage);
    }
  };

  if (initializing || (isEditMode && petStore.loading && !petStore.selectedPet)) {
    return (
      <div className="loading-container">
        <Spin size="large" />
      </div>
    );
  }

  // Show limit reached message for non-edit mode
  if (!isEditMode && petLimitReached) {
    return (
      <div className="add-edit-pet-page">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={handleCancel}
          className="back-button"
        >
          {t('pets.addEdit.backToMyAnimals')}
        </Button>

        <Card className="pet-limit-card" style={{ textAlign: 'center', padding: '40px 20px' }}>
          <h2 style={{ color: '#fa8c16', marginBottom: 16 }}>{t('pets.addEdit.limitReached.title')}</h2>
          <p style={{ fontSize: 16, marginBottom: 24 }}>
            {t('pets.addEdit.limitReached.description', { count: MAX_FREE_PETS })}
          </p>
          <p style={{ fontSize: 14, color: '#666', marginBottom: 24 }}>
            {t('pets.addEdit.limitReached.upgradeMessage')}
          </p>
          <div style={{ display: 'flex', gap: 16, justifyContent: 'center' }}>
            <Button size="large" onClick={handleCancel}>
              {t('pets.addEdit.backToMyAnimals')}
            </Button>
            <Button type="primary" size="large" disabled>
              {t('pets.addEdit.limitReached.comingSoon')}
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="add-edit-pet-page">
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={handleCancel}
        className="back-button"
      >
        {t('pets.addEdit.backToMyAnimals')}
      </Button>

      <div className="page-header">
        <h1>{isEditMode ? t('pets.addEdit.editTitle') : t('pets.addEdit.addTitle')}</h1>
        <p>{isEditMode ? t('pets.addEdit.editSubtitle') : t('pets.addEdit.addSubtitle')}</p>
      </div>

      {petStore.error && (
        <Alert
          message={t('common.error')}
          description={petStore.error}
          type="error"
          closable
          onClose={() => petStore.clearError()}
          style={{ marginBottom: 16 }}
        />
      )}

      <Card className="pet-form-card">
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          autoComplete="off"
        >
          <Form.Item
            label={t('pets.addEdit.form.name')}
            name="name"
            rules={[
              { required: true, message: t('pets.addEdit.validation.nameRequired') },
              { max: 100, message: t('pets.addEdit.validation.nameTooLong') }
            ]}
          >
            <Input size="large" placeholder={t('pets.addEdit.form.namePlaceholder')} />
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.photo')}
            help={t('pets.addEdit.form.photoHelp')}
          >
            <div className="photo-upload-section">
              <Upload
                accept="image/*"
                beforeUpload={() => false}
                onChange={handlePhotoChange}
                showUploadList={false}
              >
                <Button icon={<UploadOutlined />} size="large">
                  {t('pets.addEdit.form.selectPhoto')}
                </Button>
              </Upload>

              {photoPreview && (
                <div className="photo-preview">
                  <img src={photoPreview} alt={t('pets.addEdit.form.photoPreviewAlt')} />
                  <Button
                    danger
                    size="small"
                    onClick={() => {
                      setPhotoFile(null);
                      setPhotoPreview(null);
                    }}
                  >
                    {t('common.remove')}
                  </Button>
                </div>
              )}
            </div>
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.animalType')}
            name="animal_category"
            rules={[{ required: true, message: t('pets.addEdit.validation.typeRequired') }]}
            extra={
              <Button
                type="link"
                icon={<PlusOutlined />}
                onClick={() => setSuggestionModalVisible(true)}
                style={{ padding: 0, height: 'auto' }}
              >
                {t('pets.addEdit.form.suggestCategory')}
              </Button>
            }
          >
            <Select
              size="large"
              placeholder={t('pets.addEdit.form.selectType')}
              onChange={handleCategoryChange}
              loading={petStore.loading}
            >
              {petStore.categories.map((category) => (
                <Option key={category.id} value={category.id}>
                  {getAnimalNameWithEmoji(i18n.language === 'el' ? (category.name_el || category.name_en) : category.name_en)}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.breed')}
            name="breed"
            help={t('pets.addEdit.form.breedHelp')}
            extra={
              selectedCategory && (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => setBreedSuggestionModalVisible(true)}
                  style={{ padding: 0, height: 'auto' }}
                >
                  {t('pets.addEdit.form.suggestBreed')}
                </Button>
              )
            }
          >
            <Select
              size="large"
              placeholder={t('pets.addEdit.form.selectBreed')}
              allowClear
              disabled={!selectedCategory}
              showSearch
              filterOption={(input, option) =>
                option.children.toLowerCase().includes(input.toLowerCase())
              }
            >
              {petStore.breeds.map((breed) => (
                <Option key={breed.id} value={breed.id}>
                  {i18n.language === 'el' ? (breed.name_el || breed.name_en) : breed.name_en}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.microchip')}
            name="microchip_code"
            help={t('pets.addEdit.form.microchipHelp')}
          >
            <Input size="large" placeholder={t('pets.addEdit.form.microchipPlaceholder')} maxLength={50} />
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.birthday')}
            name="birthday"
            help={t('pets.addEdit.form.birthdayHelp')}
          >
            <DatePicker
              size="large"
              style={{ width: '100%' }}
              placeholder={t('pets.addEdit.form.selectBirthday')}
              disabledDate={(current) => current && current > dayjs().endOf('day')}
              format="MMMM D, YYYY"
            />
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.form.size')}
            name="size"
            help={t('pets.addEdit.form.sizeHelp')}
          >
            <Radio.Group size="large">
              <Radio.Button value="SMALL">{t('pets.addEdit.sizes.small')}</Radio.Button>
              <Radio.Button value="MEDIUM">{t('pets.addEdit.sizes.medium')}</Radio.Button>
              <Radio.Button value="LARGE">{t('pets.addEdit.sizes.large')}</Radio.Button>
              <Radio.Button value="EXTRA_LARGE">{t('pets.addEdit.sizes.extraLarge')}</Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item className="form-actions">
            <Button size="large" onClick={handleCancel} style={{ marginRight: 8 }}>
              {t('common.cancel')}
            </Button>
            <Button
              type="primary"
              size="large"
              htmlType="submit"
              icon={<SaveOutlined />}
              loading={submitting}
            >
              {isEditMode ? t('pets.addEdit.updateButton') : t('pets.addEdit.addButton')}
            </Button>
          </Form.Item>
        </Form>
      </Card>

      {/* Category Suggestion Modal */}
      <Modal
        title={t('pets.addEdit.categoryModal.title')}
        open={suggestionModalVisible}
        onCancel={() => {
          setSuggestionModalVisible(false);
          suggestionForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('pets.addEdit.categoryModal.description')}
        </p>
        <Form
          form={suggestionForm}
          layout="vertical"
          onFinish={handleSuggestionSubmit}
        >
          <Form.Item
            label={t('pets.addEdit.categoryModal.nameLabel')}
            name="suggested_name"
            rules={[
              { required: true, message: t('pets.addEdit.categoryModal.nameRequired') },
              { max: 100, message: t('pets.addEdit.validation.nameTooLong') }
            ]}
          >
            <Input
              size="large"
              placeholder={t('pets.addEdit.categoryModal.namePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.categoryModal.descriptionLabel')}
            name="description"
            help={t('pets.addEdit.categoryModal.descriptionHelp')}
          >
            <TextArea
              rows={3}
              placeholder={t('pets.addEdit.categoryModal.descriptionPlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingSuggestion}
              block
            >
              {t('pets.addEdit.categoryModal.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>

      {/* Breed Suggestion Modal */}
      <Modal
        title={t('pets.addEdit.breedModal.title')}
        open={breedSuggestionModalVisible}
        onCancel={() => {
          setBreedSuggestionModalVisible(false);
          breedSuggestionForm.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <p style={{ marginBottom: 16, color: '#666' }}>
          {t('pets.addEdit.breedModal.description')}
        </p>
        <Form
          form={breedSuggestionForm}
          layout="vertical"
          onFinish={handleBreedSuggestionSubmit}
        >
          <Form.Item
            label={t('pets.addEdit.breedModal.nameLabel')}
            name="suggested_name"
            rules={[
              { required: true, message: t('pets.addEdit.breedModal.nameRequired') },
              { max: 100, message: t('pets.addEdit.validation.nameTooLong') }
            ]}
          >
            <Input
              size="large"
              placeholder={t('pets.addEdit.breedModal.namePlaceholder')}
            />
          </Form.Item>

          <Form.Item
            label={t('pets.addEdit.breedModal.descriptionLabel')}
            name="description"
            help={t('pets.addEdit.breedModal.descriptionHelp')}
          >
            <TextArea
              rows={3}
              placeholder={t('pets.addEdit.breedModal.descriptionPlaceholder')}
              maxLength={500}
              showCount
            />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              loading={submittingBreedSuggestion}
              block
            >
              {t('pets.addEdit.breedModal.submit')}
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
});

export default AddEditPetPage;
